<div class="wrapper">
  <!-- The drawings detail header (Figma 33128:76594): ‹ back, what this page reviews, how -->
  <app-drawings-page-header title="Review redlines" backLabel="Back to Drawings" backTestId="drawing-carry-back" titleTestId="drawing-carry-title"
    subtitle="Shared redlines on superseded sheets. Keep or reposition copies them onto the current revision; linked records stay on the old one."
    (back)="back.emit()" />

  @if (loading) {
    <p class="empty-state mat-body-1" role="status">Loading redlines…</p>
  } @else if (!sections.length) {
    <p class="empty-state mat-body-1" data-testid="drawing-carry-empty">No redlines wait for review.</p>
  }

  @for (section of sections; track section.sheetUuid) {
    <section class="carry-section" data-testid="drawing-carry-section">
      <div class="section-heading">
        <h3 class="mat-body-1 section-name">
          {{ section.number }}@if (section.title) { · {{ section.title }}} · Rev {{ section.offer.from?.label }} → Rev {{ section.offer.to?.label }}
        </h3>
        <button type="button" class="link-button mat-body-1" (click)="openRevision.emit(section.to.uuid)">Open Rev {{ section.offer.to?.label }}</button>
      </div>
      <div class="table-scroll-wrapper">
        <table mat-table [dataSource]="section.offer.items" class="table">
          <ng-container matColumnDef="thumb">
            <th mat-header-cell *matHeaderCellDef>Redline</th>
            <td mat-cell *matCellDef="let item">
              <span class="crop" [attr.aria-label]="'Redline by ' + item.created_by?.name">
                @if (crop(item.markup_uuid); as c) {
                  <span class="crop-page" [style.width.%]="c.width" [style.left.%]="c.left" [style.top.%]="c.top">
                    <img class="crop-image" [src]="section.from.preview_url" alt="" loading="lazy" />
                    @if (item.preview_url) { <img class="crop-ink" [src]="item.preview_url" alt="" loading="lazy" /> }
                  </span>
                } @else {
                  <span class="crop-page crop-page--whole">
                    <img class="crop-image" [src]="section.from.preview_url" alt="" loading="lazy" />
                    @if (item.preview_url) { <img class="crop-ink" [src]="item.preview_url" alt="" loading="lazy" /> }
                  </span>
                }
              </span>
            </td>
          </ng-container>
          <ng-container matColumnDef="type">
            <th mat-header-cell *matHeaderCellDef>Type</th>
            <td mat-cell *matCellDef="let item"><span class="status-badge type-chip" [attr.data-status]="item.kind === 'as_built' ? (item.review_status || 'open') : null"
              data-testid="drawing-carry-type">{{ typeLabel(item) }}</span></td>
          </ng-container>
          <ng-container matColumnDef="author">
            <th mat-header-cell *matHeaderCellDef>Author</th>
            <td mat-cell *matCellDef="let item">{{ item.created_by?.name || '—' }}</td>
          </ng-container>
          <ng-container matColumnDef="date">
            <th mat-header-cell *matHeaderCellDef>Date</th>
            <td mat-cell *matCellDef="let item">{{ item.created_at | date: 'MMM d, y' }}</td>
          </ng-container>
          <ng-container matColumnDef="record">
            <th mat-header-cell *matHeaderCellDef>Linked record</th>
            <td mat-cell *matCellDef="let item">{{ linkLabel(item) }}</td>
          </ng-container>
          <ng-container matColumnDef="action">
            <th mat-header-cell *matHeaderCellDef>Action</th>
            <td mat-cell *matCellDef="let item">
              <mat-radio-group class="carry-actions" color="primary" [value]="decisions.get(item.markup_uuid) ?? null" [disabled]="!canPublish || applying"
                [attr.aria-label]="'Action for the redline by ' + item.created_by?.name" (change)="decide(item.markup_uuid, $event.value)">
                @for (a of actions; track a.value) {
                  <mat-radio-button [value]="a.value" [disabled]="a.value === 'reposition' && !canvasOf(item.markup_uuid)"
                    [attr.data-testid]="'drawing-carry-' + a.value">{{ a.label }}</mat-radio-button>
                }
              </mat-radio-group>
              @if (decisions.get(item.markup_uuid) === 'reposition') {
                <button type="button" class="link-button mat-caption nudge-link" (click)="openNudge(section, item)">Offset {{ offsetText(item.markup_uuid) }}</button>
              }
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="['thumb', 'type', 'author', 'date', 'record', 'action']"></tr>
          <tr mat-row *matRowDef="let item; columns: ['thumb', 'type', 'author', 'date', 'record', 'action']" data-testid="drawing-carry-row"
            [class.row--nudging]="nudging?.item?.markup_uuid === item.markup_uuid"></tr>
        </table>
      </div>

      @if (nudging?.section === section) {
        <div class="nudge" data-testid="drawing-carry-nudge">
          <div class="nudge-head">
            <span class="mat-body-1 nudge-title">Reposition the redline by {{ nudging.item.created_by?.name }}</span>
            <span class="secondary-text mat-caption">Drag the copy on Rev {{ section.offer.to?.label }}, or use the arrow keys (Shift for bigger steps). Offset {{ offsetText(nudging.item.markup_uuid) }}</span>
          </div>
          <div class="nudge-panes">
            <div class="nudge-pane">
              <span class="nudge-label">Rev {{ section.offer.from?.label }}</span>
              <div class="nudge-page">
                <img class="nudge-image" [src]="section.from.preview_url" alt="" draggable="false" />
                @if (nudging.item.preview_url) { <img class="nudge-ink" [src]="nudging.item.preview_url" alt="" draggable="false" /> }
              </div>
            </div>
            <div class="nudge-pane">
              <span class="nudge-label">Rev {{ section.offer.to?.label }}</span>
              <div class="nudge-page nudge-page--target" tabindex="0" role="application" data-testid="drawing-carry-nudge-target"
                [attr.aria-label]="'Move the redline copy on Rev ' + section.offer.to?.label + ' with the arrow keys'"
                (keydown)="onNudgeKey($event, nudging.item.markup_uuid)" (pointerdown)="onNudgePointerDown($event, nudging.item.markup_uuid)"
                (pointermove)="onNudgePointerMove($event, nudging.item.markup_uuid)" (pointerup)="onNudgePointerUp()" (pointercancel)="onNudgePointerUp()">
                <img class="nudge-image" [src]="section.to.preview_url" alt="" draggable="false" />
                @if (nudging.item.preview_url) {
                  <img class="nudge-ink nudge-ghost" data-testid="drawing-carry-ghost" [src]="nudging.item.preview_url" alt="" draggable="false"
                    [style.transform]="'translate(' + nudgeOf(nudging.item.markup_uuid).x * 100 + '%, ' + nudgeOf(nudging.item.markup_uuid).y * 100 + '%)'" />
                }
              </div>
            </div>
          </div>
          <div class="nudge-actions">
            <button type="button" class="link-button mat-body-1" (click)="resetNudge(nudging.item.markup_uuid)">Reset</button>
            <app-secondary-button buttonId="drawing-carry-nudge-done" (handleClick)="closeNudge()">Done</app-secondary-button>
          </div>
        </div>
      }
    </section>
  }

  @if (sections.length) {
    <div class="review-footer">
      <span class="secondary-text mat-body-1">{{ decidedCount }} of {{ offeredCount }} decided</span>
      <app-primary-button buttonId="drawing-carry-apply" data-testid="drawing-carry-apply" [isLoading]="applying" [isDisabled]="!canApply" (handleClick)="apply()">
        Apply
      </app-primary-button>
    </div>
  }
</div>
